import React, { Component, ErrorInfo, ReactNode, useCallback } from 'react';
import { Routes, Route, Navigate } from 'react-router-dom';
import { RouteGuard, setCredentials } from '@hs/shared';
import { useAppSelector, useAppDispatch } from '@/stores/hooks';
import { RouteTitleResolverProvider } from '@/contexts/RouteTitleResolverContext';
import { matchRouteTitleFromConfig } from '@/lib/match-route-title';
import { trpc } from '@/lib/trpc';
import { appRoutes } from '@/routes';

/**
 * 错误边界：捕获渲染错误
 */
class ErrorBoundary extends Component<
  { children: ReactNode },
  { hasError: boolean; error: Error | null }
> {
  constructor(props: { children: ReactNode }) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error: Error) {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    console.error('ErrorBoundary caught an error:', error, errorInfo);
  }

  render() {
    if (this.state.hasError) {
      return (
        <div style={{ padding: '2rem', textAlign: 'center' }}>
          <h1>出错了</h1>
          <p>{this.state.error?.message}</p>
          <pre
            style={{
              textAlign: 'left',
              background: '#f5f5f5',
              padding: '1rem',
              borderRadius: '4px',
            }}
          >
            {this.state.error?.stack}
          </pre>
        </div>
      );
    }

    return this.props.children;
  }
}

/**
 * App 应用主组件
 * 
 * ✅ 受保护路由：RouteGuard 检查认证状态
 * ✅ 逻辑简单清晰
 */
function App() {
  const dispatch = useAppDispatch();
  const isAuthenticated = useAppSelector((state) => state.auth.isAuthenticated);
  const user = useAppSelector((state) => state.auth.user);

  const guardProps = {
    isAuthenticated,
    user,
    trpc,
    dispatch,
    setCredentials,
    isHomeApp: false,
  };

  const resolveRouteTitle = useCallback(
    (pathname: string) => matchRouteTitleFromConfig(appRoutes, pathname),
    [],
  );

  // 递归渲染路由
  const renderRoute = (route: (typeof appRoutes)[0], key?: string): React.ReactElement => {
    const routeKey = key || route.path;

    // 处理嵌套路由
    if (route.children && route.children.length > 0) {
      return (
        <Route
          key={routeKey}
          path={route.path}
          element={
            <RouteGuard meta={route.meta} {...guardProps}>
              {route.element}
            </RouteGuard>
          }
        >
          {route.children.map((child) => {
            if (child.index) {
              return (
                <Route
                  key={`${routeKey}-index`}
                  index
                  element={
                    <RouteGuard meta={child.meta} {...guardProps}>
                      {child.element}
                    </RouteGuard>
                  }
                />
              );
            }
            // 递归处理子路由的子路由
            if (child.children && child.children.length > 0) {
              return renderRoute(child, `${routeKey}-${child.path}`);
            }
            const childRequiresAuth = child.meta?.requiresAuth !== false;
            const childElement = childRequiresAuth ? (
              <RouteGuard meta={child.meta} {...guardProps}>
                {child.element}
              </RouteGuard>
            ) : (
              child.element
            );
            return (
              <Route
                key={`${routeKey}-${child.path}`}
                path={child.path}
                element={childElement}
              />
            );
          })}
        </Route>
      );
    }

    // 普通路由
    const { path, element, meta } = route;
    const requiresAuth = meta?.requiresAuth !== false;

    const guardedElement = requiresAuth ? (
      <RouteGuard meta={meta} {...guardProps}>
        {element}
      </RouteGuard>
    ) : (
      element
    );

    return <Route key={routeKey} path={path} element={guardedElement} />;
  };

  return (
    <RouteTitleResolverProvider resolve={resolveRouteTitle}>
      <ErrorBoundary>
        <Routes>
          {appRoutes.map((route) => renderRoute(route))}

          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </ErrorBoundary>
    </RouteTitleResolverProvider>
  );
}

export default App;
